/**
 * Reusable LLM form. Used for both create (`/llms/new`) and edit
 * (`/llms/$id`). The two routes wrap this with their own initial-data
 * + submit handlers — the form itself is purely a controlled-inputs
 * surface.
 *
 * The API-key field is the security-sensitive bit. On edit, we never
 * receive the stored key from the server (only `has_api_key`); the
 * field renders empty with placeholder text indicating the existing
 * key is preserved unless overwritten. Submitting blank = no rotation.
 */

import { Eye, EyeOff } from "lucide-react";
import * as React from "react";

import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Provider = components["schemas"]["AdminLLMCreateRequest"]["provider"];

const PROVIDERS = ["anthropic", "google", "openai"] as const satisfies readonly Provider[];

// Compile-time exhaustiveness: if the OpenAPI provider enum gains a value,
// this errors until PROVIDERS is updated.
type _Exhaustive = Exclude<Provider, (typeof PROVIDERS)[number]> extends never ? true : false;
const _checkExhaustive: _Exhaustive = true;
void _checkExhaustive;

export type LlmFormValues = {
  code: string;
  name: string;
  provider: Provider;
  provider_model: string;
  is_active: boolean;
  notes: string;
  api_key: string; // empty = don't rotate (edit) / required (create)
};

const EMPTY: LlmFormValues = {
  code: "",
  name: "",
  provider: PROVIDERS[0],
  provider_model: "",
  is_active: true,
  notes: "",
  api_key: "",
};

export function LlmForm({
  mode,
  initial,
  hasApiKey,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  mode: "create" | "edit";
  initial?: Partial<LlmFormValues>;
  hasApiKey?: boolean;
  submitting: boolean;
  error: string | null;
  onSubmit: (values: LlmFormValues) => void;
  onCancel: () => void;
}) {
  const [v, setV] = React.useState<LlmFormValues>({ ...EMPTY, ...initial });
  const [showKey, setShowKey] = React.useState(false);

  const set = <K extends keyof LlmFormValues>(key: K, value: LlmFormValues[K]) =>
    setV((prev) => ({ ...prev, [key]: value }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(v);
  }

  return (
    <form className="max-w-xl space-y-5" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="code">
          Code
          {mode === "edit" && (
            <span className="ml-2 text-xs text-zinc-500">(read-only)</span>
          )}
        </Label>
        <Input
          id="code"
          value={v.code}
          onChange={(e) => set("code", e.target.value)}
          required
          disabled={mode === "edit"}
          placeholder="stable internal id (e.g. claude-sonnet)"
        />
        <p className="text-xs text-zinc-500">
          Stable internal identifier referenced by application code. Renaming
          would break runtime calls; rename via migration instead.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={v.name}
          onChange={(e) => set("name", e.target.value)}
          required
          placeholder="display name"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="provider">Provider</Label>
          <select
            id="provider"
            value={v.provider}
            onChange={(e) => set("provider", e.target.value as Provider)}
            className="h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
          >
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="provider_model">Provider model</Label>
          <Input
            id="provider_model"
            value={v.provider_model}
            onChange={(e) => set("provider_model", e.target.value)}
            required
            placeholder="provider's model id"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="is_active"
          type="checkbox"
          checked={v.is_active}
          onChange={(e) => set("is_active", e.target.checked)}
          className="size-4"
        />
        <Label htmlFor="is_active" className="cursor-pointer">
          Active (can be assigned to prompt versions)
        </Label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          rows={2}
          value={v.notes}
          onChange={(e) => set("notes", e.target.value)}
          placeholder="optional — internal notes (model version, ratelimit profile, etc.)"
        />
      </div>

      <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
        <Label htmlFor="api_key" className="text-amber-900">
          API key{mode === "create" && <span className="ml-1">*</span>}
        </Label>
        <div className="flex gap-2">
          <Input
            id="api_key"
            type={showKey ? "text" : "password"}
            value={v.api_key}
            onChange={(e) => set("api_key", e.target.value)}
            required={mode === "create"}
            placeholder={
              mode === "edit"
                ? hasApiKey
                  ? "•••• stored — leave blank to keep"
                  : "No key on file"
                : "sk-…"
            }
            autoComplete="off"
            spellCheck={false}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="px-2"
            onClick={() => setShowKey((s) => !s)}
            aria-label={showKey ? "Hide API key" : "Show API key"}
          >
            {showKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </Button>
        </div>
        <p className="text-xs text-amber-900/80">
          The key is encrypted (Fernet) at rest in the <code>llm</code> table
          and never returned by the API. Submitting a new value here replaces
          the stored cipher.
        </p>
      </div>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : mode === "create" ? "Create LLM" : "Save changes"}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
